import { NpcAvatar } from '@/components/admin/npc/npc-card';
import { versionedDay } from '@/lib/versioned-labels';
import type { NpcBench } from '@matter_extractor/shared';
import { Button } from '@pms-digital/design-system';
import { Link } from '@tanstack/react-router';
import type { ReactNode } from 'react';

/**
 * La liste des bancs (npc.md §5; maquette `Bibliotheque`, planche « c. L'onglet Bancs »): one row
 * per bench - its name, its NPC as a stack of initials, « lancé 4 fois », the date of its last
 * series, and « Modifier ».
 *
 * « Lancer » is `renderLaunch`, the slot the box of §6.1 plugs into, drawn before « Modifier » and
 * given the bench. A bench whose NPC are all retired does not launch (`bench-empty`), which is the
 * box's to say.
 *
 * A retired bench is listed greyed under its own heading when the page asks for them, with the one
 * gesture that brings it back; it is never offered a launch.
 */

export interface NpcBenchesListProps {
  benches: readonly NpcBench[];
  /** The retired ones are shown (the page's checkbox). */
  showRetired: boolean;
  restoringId: string | null;
  onRestore: (bench: NpcBench) => void;
  /** « Lancer » (§6.1), drawn before « Modifier » on a live bench. */
  renderLaunch?: (bench: NpcBench) => ReactNode;
}

/** How many initials the stack shows before « +N ». */
const STACK = 5;

export function NpcBenchesList({
  benches,
  showRetired,
  restoringId,
  onRestore,
  renderLaunch,
}: NpcBenchesListProps) {
  const shown = benches.filter((bench) => showRetired || bench.retiredAt === null);
  if (shown.length === 0) {
    return (
      <div
        data-testid="npc-bancs-vide"
        className="rounded-md border-2 border-gray-300 border-dashed px-6 py-10 text-center"
      >
        <h2 className="font-semibold text-body-lg text-gray-900">Aucun banc pour l’instant</h2>
        <p className="mx-auto max-w-prose pt-1 text-gray-600 text-small">
          Un banc est un nom et une liste ordonnée de NPC, qu’on lance sur une expérience d’un seul
          geste.
        </p>
      </div>
    );
  }
  return (
    <ul data-testid="npc-bancs" className="divide-y divide-gray-200">
      {shown.map((bench) => {
        const retired = bench.retiredAt !== null;
        return (
          <li
            key={bench.id}
            data-testid="npc-banc"
            data-retired={retired ? 'true' : 'false'}
            className="flex flex-wrap items-center justify-between gap-x-6 gap-y-3 py-4"
          >
            <div className={`min-w-0 space-y-1.5 ${retired ? 'opacity-60' : ''}`}>
              <Link
                to="/admin/npc"
                search={{ onglet: 'bancs', banc: bench.id }}
                className="font-semibold text-bleu-nuit text-body-lg underline-offset-2 hover:underline"
              >
                {bench.name}
              </Link>
              <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                <ul aria-hidden="true" className="-space-x-1.5 flex">
                  {bench.npcs.slice(0, STACK).map((member) => (
                    <li key={member.id} className="rounded-full ring-2 ring-white">
                      <NpcAvatar firstName={member.firstName} kind={member.kind} size="sm" />
                    </li>
                  ))}
                  {bench.npcs.length > STACK ? (
                    <li className="inline-flex size-7 items-center justify-center rounded-full bg-gray-100 font-bold text-caption text-gray-700 ring-2 ring-white">
                      +{bench.npcs.length - STACK}
                    </li>
                  ) : null}
                </ul>
                <p data-testid="npc-banc-faits" className="text-gray-600 text-small">
                  {factsOf(bench)}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {retired ? (
                <Button
                  type="button"
                  size="sm"
                  compact
                  variant="accent"
                  data-testid="npc-banc-restaurer"
                  disabled={restoringId === bench.id}
                  onClick={() => onRestore(bench)}
                >
                  Restaurer
                </Button>
              ) : (
                <>
                  {renderLaunch?.(bench)}
                  <Link
                    to="/admin/npc"
                    search={{ onglet: 'bancs', banc: bench.id }}
                    data-testid="npc-banc-modifier"
                    className="inline-flex h-10 items-center rounded-md border-2 border-gray-300 bg-white px-4 font-semibold text-gray-700 text-small no-underline hover:bg-gray-50"
                  >
                    Modifier
                  </Link>
                </>
              )}
            </div>
          </li>
        );
      })}
    </ul>
  );
}

/** « 8 NPC · lancé 4 fois · dernière série le 24 oct. 2026 », « jamais lancé » when it never was. */
export function factsOf(bench: NpcBench): string {
  const stack = `${bench.npcs.length} NPC`;
  const launched = bench.launchCount === 0 ? 'jamais lancé' : `lancé ${bench.launchCount} fois`;
  const last =
    bench.lastSeriesAt === null ? null : `dernière série le ${versionedDay(bench.lastSeriesAt)}`;
  return [stack, launched, last].filter((part) => part !== null).join(' · ');
}
